// ─── Template : FEUILLE (modale du bas) ───────────────────────────
// Gabarit unique de TOUTES les modales de la suite : voile + feuille ancrée en
// bas, poignée + en-tête standard (icône + titre + 💾 + ✕), corps défilant.
// Chaque modale de l'app se contente de fournir son titre, son action et son
// contenu — la hauteur, les marges, la disquette grisée, le bouton fermer et la
// convention « 💾 puis ✕ » vivent ICI, donc restent identiques partout.
//
//   <Feuille visible titre="…" emoji="📦"
//     onFermer={…}
//     action={{ icone:'💾', onPress:enregistrer, disabled:!modifie, label:'Enregistrer' }}>
//     …contenu défilant…
//   </Feuille>
//
// `hauteur` : fraction de l'écran à l'OUVERTURE (défaut 0.82 ; petites → 0.4).
// `sansScroll` : rendre le contenu sans ScrollView (contenu déjà scrollable).
//
// PALIERS (natif, TOUTES les feuilles) : la feuille s'accroche à plusieurs
// positions — palier bas (50 %, ou la hauteur d'ouverture si elle est plus
// compacte) / `hauteur` / 82 % / plein écran (sous l'encoche). Balayer
// l'en-tête vers le bas descend d'un palier puis FERME depuis le palier bas
// (un lancer rapide ferme directement) ; vers le haut remonte jusqu'au plein
// écran. Comportement app-wide identique, même pour les feuilles compactes.
//
// IMPORTANT (natif) : on rend un CALQUE ABSOLU, PAS un <Modal> natif — un
// GestureDetector à l'intérieur d'un <Modal> RN ne reçoit pas le geste de façon
// fiable (hiérarchie native séparée du GestureHandlerRootView racine). Sur le
// web, un <Modal> convient (pas de geste) et gère mieux le plein écran.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, TouchableOpacity, TouchableWithoutFeedback, ScrollView, Modal,
  StyleSheet, Animated, Platform, useWindowDimensions, Keyboard,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUI } from '../theme/PreferencesUI';
import { useClavierVisible } from './useClavier';
import PageHeader from './PageHeader';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const NATIF = Platform.OS !== 'web';

export default function Feuille({
  visible, titre, emoji, onFermer, action, boutons, hauteur = 0.82, sansScroll = false, scrollRef, children,
}) {
  const UI = useUI();
  const s = useMemo(() => makeStyles(UI, hauteur), [UI, hauteur]);
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const clavierVisible = useClavierVisible();

  // Hauteur ANIMÉE en pixels (JS driver : `height` est une prop de layout).
  // Le calque natif ne couvre que la zone de l'onglet (PAS la barre du bas) :
  // les paliers se basent donc sur sa hauteur MESURÉE, jamais sur la fenêtre —
  // sinon le plein écran déborde au-dessus de l'écran.
  const px = useRef(new Animated.Value(0)).current;
  const [calqueH, setCalqueH] = useState(0);
  const base = NATIF && calqueH ? calqueH : height;
  const maxPx = Math.max(1, base - insets.top - 8);              // plein écran (sous l'encoche)
  const ouverturePx = Math.min(Math.round(hauteur * base), maxPx);
  const avecPaliers = NATIF && !!onFermer;
  // Palier bas = 50 % (ou l'ouverture si plus compacte) ; paliers hauts = 82 %
  // et plein écran — une feuille compacte peut donc TOUJOURS être agrandie.
  const paliersPx = useMemo(() => (avecPaliers
    ? [...new Set([
        Math.min(ouverturePx, Math.round(0.5 * base)),
        ouverturePx,
        Math.min(Math.round(0.82 * base), maxPx),
        maxPx,
      ])].sort((a, b) => a - b)
    : [ouverturePx]), [avecPaliers, base, ouverturePx, maxPx]);
  const palierRef = useRef(ouverturePx);                          // palier COURANT (px)

  // Entrée : la feuille pousse depuis le bas jusqu'au palier d'ouverture.
  const etaitVisible = useRef(false);
  useEffect(() => {
    if (!NATIF) return undefined;
    if (visible && !etaitVisible.current) {
      etaitVisible.current = true;
      palierRef.current = ouverturePx;
      px.setValue(0);
      Animated.timing(px, { toValue: ouverturePx, duration: 230, useNativeDriver: false }).start();
    } else if (visible && palierRef.current > maxPx) {
      // Le calque vient d'être mesuré (plus petit que la fenêtre) : on recale
      // le palier courant sans repartir de zéro.
      palierRef.current = Math.min(ouverturePx, maxPx);
      Animated.timing(px, { toValue: palierRef.current, duration: 120, useNativeDriver: false }).start();
    }
    if (!visible) etaitVisible.current = false;
    return undefined;
  }, [visible, ouverturePx, maxPx, px]);

  if (!visible) return null;

  const fermer = () => { onFermer && onFermer(); };
  const fermerAnime = () => Animated.timing(px, { toValue: 0, duration: 170, useNativeDriver: false }).start(() => fermer());
  const allerAuPalier = (p) => { palierRef.current = p; Animated.spring(px, { toValue: p, useNativeDriver: false, speed: 16, bounciness: 3 }).start(); };

  const geste = Gesture.Pan()
    .enabled(NATIF && !!onFermer)
    .runOnJS(true)
    .activeOffsetY([-12, 12])     // vrai glissement vertical (les 2 sens)
    .onUpdate((e) => { px.setValue(Math.max(60, Math.min(maxPx, palierRef.current - e.translationY))); })
    .onEnd((e) => {
      const cur = Math.max(0, Math.min(maxPx, palierRef.current - e.translationY));
      const vy = e.velocityY;                                    // > 0 = vers le bas
      if (!avecPaliers) {
        const franchi = e.translationY > palierRef.current * 0.25 || vy > 800;
        if (franchi) fermerAnime(); else allerAuPalier(palierRef.current);
        return;
      }
      const bas = paliersPx[0];
      let cible;
      if (vy > 1400 || cur < bas - 0.12 * base) cible = null;                     // fermer
      else if (vy > 500) cible = [...paliersPx].reverse().find(p => p < palierRef.current - 10) ?? null;
      else if (vy < -500) cible = paliersPx.find(p => p > palierRef.current + 10) ?? palierRef.current;
      else cible = paliersPx.reduce((a, p) => (Math.abs(p - cur) < Math.abs(a - cur) ? p : a), paliersPx[0]);
      // Au palier bas, tirer encore franchement vers le bas ferme aussi.
      if (cible === bas && palierRef.current === bas && e.translationY > 0.12 * base) cible = null;
      if (cible == null) fermerAnime(); else allerAuPalier(cible);
    });

  const enTete = (
    <View style={s.entete}>
      <View style={s.grabZone}><View style={s.grab} /></View>
      <PageHeader titre={titre} iconeTitre={emoji} paddingTop={4} action={action}>
        {/* ⌨️ « réduire le clavier » : visible seulement quand le clavier est
            ouvert (natif). Principe app-wide via ce gabarit de modale. */}
        {clavierVisible && (
          <TouchableOpacity onPress={() => Keyboard.dismiss()} hitSlop={HIT} style={s.clavierBtn} accessibilityLabel="Baisser le clavier">
            <Text style={s.clavier}>⌨️</Text>
            <Text style={s.clavierFleche}>▾</Text>
          </TouchableOpacity>
        )}
        {boutons}
        {!!onFermer && (
          <TouchableOpacity onPress={onFermer} hitSlop={HIT} accessibilityLabel="Fermer">
            <Text style={s.fermer}>✕</Text>
          </TouchableOpacity>
        )}
      </PageHeader>
    </View>
  );

  const corps = sansScroll
    ? <View style={{ flex: 1 }}>{children}</View>
    : (
      <ScrollView ref={scrollRef} contentContainerStyle={s.corps} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag">
        {children}
        <View style={{ height: 26 }} />
      </ScrollView>
    );

  const feuille = (
    <Animated.View style={[s.feuille, NATIF && { height: px }]}>
      {NATIF && !!onFermer ? <GestureDetector gesture={geste}>{enTete}</GestureDetector> : enTete}
      {corps}
    </Animated.View>
  );

  // WEB : <Modal> (pas de geste, meilleur plein écran).
  if (!NATIF) {
    return (
      <Modal visible animationType="slide" transparent onRequestClose={onFermer}>
        <View style={s.fond}>{feuille}</View>
      </Modal>
    );
  }

  // NATIF : calque absolu (sous le GestureHandlerRootView racine → geste fiable).
  return (
    <View style={[StyleSheet.absoluteFill, s.calque]} onLayout={(e) => setCalqueH(e.nativeEvent.layout.height)}>
      <TouchableWithoutFeedback onPress={fermer} accessibilityLabel="Fermer">
        <View style={[StyleSheet.absoluteFill, s.voile]} />
      </TouchableWithoutFeedback>
      {feuille}
    </View>
  );
}

const makeStyles = (UI, hauteur) => StyleSheet.create({
  fond:    { flex: 1, backgroundColor: UI.overlay, justifyContent: 'flex-end' },
  calque:  { justifyContent: 'flex-end', zIndex: 300, elevation: 48 },
  voile:   { backgroundColor: UI.overlay },
  // Web : hauteur fixe en % ; natif : hauteur pilotée par l'animation (paliers).
  feuille: { height: `${Math.round(hauteur * 100)}%`, backgroundColor: UI.bg,
             borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' },
  entete:  { },
  grabZone:{ alignItems: 'center', paddingTop: 8, paddingBottom: 2 },
  grab:    { width: 38, height: 4, borderRadius: 999, backgroundColor: UI.border },
  corps:   { padding: 16 },
  fermer:  { fontSize: 20, color: UI.accent },
  clavierBtn: { flexDirection: 'row', alignItems: 'center' },
  clavier: { fontSize: 18 },
  clavierFleche: { fontSize: 12, color: UI.accent, marginLeft: 1, marginTop: 3, fontWeight: '900' },
});
